import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Sparkle, ArrowLeft, CheckCircle, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const scorePassword = (pw) => {
  if (!pw) return { score: 0, label: "", tone: "slate", checks: {} };
  const checks = {
    len: pw.length >= 8,
    upper: /[A-Z]/.test(pw),
    lower: /[a-z]/.test(pw),
    digit: /\d/.test(pw),
    symbol: /[^A-Za-z0-9]/.test(pw),
    long: pw.length >= 12,
  };
  const hits = Object.values(checks).filter(Boolean).length;
  let score = 0;
  if (checks.len) score = 1;
  if (checks.len && hits >= 3) score = 2;
  if (checks.len && hits >= 4) score = 3;
  if (checks.long && hits >= 5) score = 4;
  const labels = ["Too short", "Weak", "Fair", "Strong", "Excellent"];
  const tones = ["rose", "rose", "amber", "emerald", "emerald"];
  return { score, label: labels[score], tone: tones[score], checks };
};

const TONE_BG = { slate: "bg-slate-200", rose: "bg-rose-500", amber: "bg-amber-500", emerald: "bg-emerald-500" };
const TONE_TEXT = { slate: "text-slate-500", rose: "text-rose-600", amber: "text-amber-600", emerald: "text-emerald-600" };

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const strength = useMemo(() => scorePassword(password), [password]);
  const minOk = password.length >= 8;
  const matchOk = confirm.length > 0 && password === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!minOk) { toast.error("Password must be at least 8 characters"); return; }
    if (strength.score < 2) { toast.error("Pick a stronger password (mix case, digits or symbols)"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    if (!token) { toast.error("Missing token — open the link from your email"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to reset");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex w-1/2 bg-slate-900 text-white flex-col justify-between p-12">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-blue-600 flex items-center justify-center">
            <Sparkle size={20} weight="fill" />
          </div>
          <div className="font-heading font-bold text-xl">Kollab</div>
        </div>
        <div className="max-w-md">
          <div className="text-xs uppercase tracking-widest text-blue-400 mb-3">Set a new password</div>
          <h1 className="font-heading text-5xl font-bold leading-tight mb-4">
            One quick step and you're back in.
          </h1>
          <p className="text-slate-400 text-base leading-relaxed">
            Pick something you'll remember but nobody else could guess. At least 8 characters, and mix a few kinds.
          </p>
        </div>
        <div className="text-xs text-slate-500">Kollab · Password reset</div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-sm">
          <Link to="/login" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-6" data-testid="back-to-login">
            <ArrowLeft size={12} /> Back to sign in
          </Link>

          {done ? (
            <div>
              <div className="w-12 h-12 rounded-md bg-emerald-50 flex items-center justify-center mb-4">
                <CheckCircle size={22} className="text-emerald-600" weight="duotone" />
              </div>
              <h2 className="font-heading text-3xl font-bold text-slate-900">All set</h2>
              <p className="text-sm text-slate-600 mt-2">Your password has been updated. Redirecting to sign in…</p>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Almost there</div>
                <h2 className="font-heading text-3xl font-bold text-slate-900">Choose a new password</h2>
              </div>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">New password</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showPw ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      required
                      className="pr-10"
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((s) => !s)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
                      data-testid="toggle-password-visibility"
                      tabIndex={-1}
                    >
                      {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                    </button>
                  </div>

                  {password.length > 0 && (
                    <div className="mt-2 space-y-2" data-testid="password-strength">
                      <div className="flex gap-1">
                        {[0, 1, 2, 3].map((i) => (
                          <div
                            key={i}
                            className={`h-1.5 flex-1 rounded-full transition-colors ${
                              i < strength.score ? TONE_BG[strength.tone] : "bg-slate-200"
                            }`}
                          />
                        ))}
                      </div>
                      <div className={`text-[11px] ${TONE_TEXT[strength.tone]}`} data-testid="password-strength-label">
                        {strength.label}
                      </div>
                      <ul className="text-[11px] text-slate-500 grid grid-cols-2 gap-x-3 gap-y-0.5">
                        <li className={strength.checks.len ? "text-emerald-600" : ""}>{strength.checks.len ? "✓" : "•"} 8+ characters</li>
                        <li className={strength.checks.upper ? "text-emerald-600" : ""}>{strength.checks.upper ? "✓" : "•"} Uppercase letter</li>
                        <li className={strength.checks.lower ? "text-emerald-600" : ""}>{strength.checks.lower ? "✓" : "•"} Lowercase letter</li>
                        <li className={strength.checks.digit ? "text-emerald-600" : ""}>{strength.checks.digit ? "✓" : "•"} Number</li>
                        <li className={strength.checks.symbol ? "text-emerald-600" : ""}>{strength.checks.symbol ? "✓" : "•"} Symbol (!@#…)</li>
                        <li className={strength.checks.long ? "text-emerald-600" : ""}>{strength.checks.long ? "✓" : "•"} 12+ chars (bonus)</li>
                      </ul>
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Confirm password</Label>
                  <Input
                    type={showPw ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Re-enter it"
                    required
                    className="mt-1"
                    data-testid="reset-confirm-input"
                  />
                  {confirm.length > 0 && !matchOk && (
                    <div className="text-[11px] text-rose-600 mt-1" data-testid="password-mismatch">Passwords don't match yet</div>
                  )}
                </div>
                <Button
                  type="submit"
                  disabled={loading || !token || !minOk || strength.score < 2 || !matchOk}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11"
                  data-testid="reset-submit-btn"
                >
                  {loading ? "Updating…" : "Update password"}
                </Button>
                {!token && (
                  <div className="text-xs text-rose-600 text-center">
                    Missing token — please open the reset link from your email.
                  </div>
                )}
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
